/**
 * How the deck fits the window: pure arithmetic over the sizes the page
 * measures, applied by main.ts as each part's zoom. Nothing ever grows past its
 * natural size.
 */

export interface Box {
  readonly width: number
  readonly height: number
}

/** The deck's parts at their natural sizes, in the orientation they are laid out in. */
export interface Parts {
  readonly keys: Box
  readonly knobs: Box
  /** The canvas's side, the Grid and the open margin round it. */
  readonly canvas: number
  readonly margin: number
  /** The margin always kept round the Grid, however tight the room. */
  readonly keep: number
}

export interface Fit {
  /** Each part's zoom, at most 1. */
  readonly keys: number
  readonly knobs: number
  readonly grid: number
  /** How much of the canvas's margin, in its own px, the parts beside it may cover. */
  readonly eat: number
}

/** The zooms that fit `parts` into `room`, laid out in a column if `portrait`, else a row. */
export const fit = (room: Box, parts: Parts, portrait: boolean): Fit => {
  // Each column shrinks to the room along it; the Grid gets the rest across.
  const along = portrait ? room.width : room.height
  const keys = Math.min(1, along / (portrait ? parts.keys.width : parts.keys.height))
  const knobs = Math.min(1, along / (portrait ? parts.knobs.width : parts.knobs.height))
  const left = portrait
    ? room.height - parts.keys.height * keys - parts.knobs.height * knobs
    : room.width - parts.keys.width * keys - parts.knobs.width * knobs
  const side = Math.min(left, along)
  const eat = Math.max(0, Math.min(parts.margin - parts.keep, (parts.canvas - side) / 2))
  return { keys, knobs, grid: Math.min(1, side / (parts.canvas - eat * 2)), eat }
}
